'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import dayjs from 'dayjs';
import { numberShorthand } from '@benzinga/utils';
import type { GovernmentMember } from '@benzinga/gov-trades';

import { memberDisplayNameToProfileSlug } from './memberProfileSlug';
import styles from './MemberCard.module.scss';

export type MemberCardProps = GovernmentMember & { ttm_return?: number };

export type PartyTone = 'Dem' | 'Rep' | 'Ind';

export const partyTone = (party: string | undefined | null): PartyTone => {
  if (party === 'R') return 'Rep';
  if (party === 'D') return 'Dem';
  return 'Ind';
};

const partyBadgeLabel = (party: string | undefined | null): string => {
  if (party === 'R' || party === 'D' || party === 'I') return party;
  return party?.trim() || '—';
};

const FALLBACK_HEADSHOT = '/next-assets/images/no_picture.png';

const toneClass: Record<PartyTone, string> = {
  Dem: styles.toneDem,
  Rep: styles.toneRep,
  Ind: styles.toneInd,
};

export const MemberCard: React.FC<MemberCardProps> = ({ filer_info, stats, ttm_return }) => {
  const { chamber, display_name, headshot, member_id, party, state } = filer_info ?? {};
  const { date_of_last_recorded_trade, number_of_trades, total_volume_of_trades } = stats ?? {};
  const tone = partyTone(party);
  const href = `/government-trades/members/${member_id || memberDisplayNameToProfileSlug(display_name)}`;
  const [imgSrc, setImgSrc] = useState(headshot?.trim() || FALLBACK_HEADSHOT);

  const hasReturn = typeof ttm_return === 'number' && Number.isFinite(ttm_return);
  const returnPct = hasReturn ? ttm_return * 100 : 0;
  const returnPositive = returnPct >= 0;

  return (
    <Link className={`${styles.card} ${toneClass[tone]}`} href={href} data-testid="gov-trades-member-card">
      <div className={styles.media}>
        <img
          alt=""
          className={styles.photo}
          src={imgSrc}
          onError={() => {
            if (imgSrc !== FALLBACK_HEADSHOT) setImgSrc(FALLBACK_HEADSHOT);
          }}
        />
        <span className={styles.fade} aria-hidden />
        <span className={styles.edge} aria-hidden />
        {hasReturn ? (
          <span className={`${styles.ret} ${returnPositive ? styles.retPos : styles.retNeg}`}>
            {returnPositive ? '+' : ''}
            {returnPct.toFixed(1)}%
          </span>
        ) : null}
      </div>

      <div className={styles.body}>
        <div>
          <div className={styles.name}>{display_name}</div>
          <div className={styles.meta}>
            <span>{chamber}</span>
            <span className={styles.party} title={party ?? undefined}>
              {partyBadgeLabel(party)}
            </span>
            {state ? <span className={styles.state}>{state}</span> : null}
          </div>
        </div>

        <div className={styles.stats}>
          <div className={styles.stat}>
            <span className={styles.statLabel}>Trades</span>
            <span className={styles.statValue}>
              {typeof number_of_trades === 'number' ? number_of_trades.toLocaleString() : '—'}
            </span>
          </div>
          <div className={styles.stat}>
            <span className={styles.statLabel}>Volume</span>
            <span className={styles.statValue}>
              {typeof total_volume_of_trades === 'number' ? `$${numberShorthand(total_volume_of_trades, 1)}` : '—'}
            </span>
          </div>
          <div className={styles.stat}>
            <span className={styles.statLabel}>Last Traded</span>
            <span className={styles.statValue}>
              {date_of_last_recorded_trade ? dayjs(date_of_last_recorded_trade).format('MMM DD, YY') : '—'}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
};
